// Owned sctmgUtils runtime.
//
// Responsibilities:
//   - Own history.pushState and popstate. Adapters must not touch URLs.
//   - Load the route registry (routes.json) and resolve the active feature.
//   - Honour the mount/unmount lifecycle for each feature.
//   - Bind the shell scaffold (sidebar + content container) that ships in
//     src/index.html so the visitor sees navigation immediately while the
//     initial chunk loads. bootShell() is idempotent: when the markup is
//     already mounted it is reused; when it is missing it is built.
//
// This module deliberately avoids any reference to the legacy web
// runtime or the auto-update helper: sctmgUtils is owned end to end and
// only reads from local manifests and feature chunks shipped in the
// build.

import routesManifest from './routes.json' with { type: 'json' };
import { APP_VERSION } from './version.js';
import { getCurrentUser as adapterGetCurrentUser, onAuthStateChanged as adapterOnAuthStateChanged, signInWithGoogle as adapterSignInWithGoogle, consumeGoogleRedirect as adapterConsumeGoogleRedirect, signOut as adapterSignOut } from './adapters/firebase.js';

const ROUTES = Array.isArray(routesManifest) ? routesManifest : [];
const ROUTES_BY_ID = new Map(ROUTES.map((route) => [route.id, route]));
const ROUTES_BY_PATH = new Map(ROUTES.map((route) => [route.path, route]));

const INITIAL_ROUTE_ID = (ROUTES.find((route) => route.initial) || {}).id || 'changelog';
const INITIAL_ROUTE_PATH = (ROUTES_BY_ID.get(INITIAL_ROUTE_ID) || {}).path || '/changelog';

const loadedModules = new Map();   // id -> module
const loadedPromises = new Map();  // id -> Promise<module>
let currentRoute = null;           // { id, path, instance, route }
let shellRoot = null;
let shellContainer = null;         // .sctmg-shell wrapper
let shellSidebar = null;           // .sctmg-shell__sidebar nav
let shellNavList = null;           // <ul data-shell-nav>
let shellContent = null;           // main#app-root
let drawerBound = false;
let language = 'es';

function normalisePath(pathname) {
  if (!pathname || pathname === '/') return INITIAL_ROUTE_PATH;
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function findRouteByPath(pathname) {
  const normalised = normalisePath(pathname);
  const exact = ROUTES_BY_PATH.get(normalised);
  if (exact) return exact;
  const factionMatch = normalised.match(/^\/factions\/(zerg|terran|protoss)(?:\/(faction-cards|units|tactical-cards)(?:\/([^/]+))?)?$/);
  if (factionMatch) {
    return {
      ...ROUTES_BY_ID.get('factions'), path: normalised,
      initialFaction: factionMatch[1], initialCategory: factionMatch[2], initialCardId: factionMatch[3] && decodeURIComponent(factionMatch[3]),
    };
  }
  if (normalised.startsWith('/campaigns/')) {
    return { ...ROUTES_BY_ID.get('campaigns'), path: normalised, campaignId: decodeURIComponent(normalised.slice('/campaigns/'.length)) };
  }
  if (normalised.startsWith('/rules/')) {
    return { ...ROUTES_BY_ID.get('rules'), path: normalised };
  }
  return ROUTES_BY_ID.get(INITIAL_ROUTE_ID);
}

const ROUTE_LOADERS = Object.freeze({
  rules: () => import('./features/rules/index.js'),
  factions: () => import('./features/factions/index.js'),
  'army-builder': () => import('./features/army-builder/index.js'),
  faqs: () => import('./features/faqs/index.js'),
  'mission-cards': () => import('./features/mission-cards/index.js'),
  'deployment-maps': () => import('./features/deployment-maps/index.js'),
  campaigns: () => import('./features/campaigns/index.js'),
  home: () => import('./features/changelog/index.js'),
});

function loadRouteModule(route) {
  if (loadedModules.has(route.id)) return Promise.resolve(loadedModules.get(route.id));
  if (loadedPromises.has(route.id)) return loadedPromises.get(route.id);
  const loader = ROUTE_LOADERS[route.id];
  if (typeof loader !== 'function') return Promise.reject(new Error(`Unknown route module: ${route.id}`));
  const pending = loader()
    .then((mod) => {
      loadedModules.set(route.id, mod);
      loadedPromises.delete(route.id);
      return mod;
    })
    .catch((error) => {
      loadedPromises.delete(route.id);
      throw error;
    });
  loadedPromises.set(route.id, pending);
  return pending;
}

// Find the existing shell scaffold if the owned markup is already in
// the DOM. Returns { container, sidebar, navList, content } or null when
// none is present.
//
// The owned markup declares the sidebar as <aside class="panel"
// id="sctmg-shell-sidebar"> with <button data-shell-nav data-route="...">
// entries; there is no <ul data-shell-nav>. The legacy ul form is
// recognised as a fallback for tests and SSR.
function detectOwnedShell(root) {
  const doc = (root && root.ownerDocument) || (typeof document !== 'undefined' ? document : null);
  if (!doc) return null;

  // Prefer a .sctmg-shell container that already wraps the root.
  let container = null;
  if (root && root.classList && root.classList.contains('sctmg-shell')) {
    container = root;
  } else if (root && root.closest) {
    container = root.closest('.sctmg-shell');
  }
  if (!container) container = doc.querySelector('.sctmg-shell');

  if (!container) return null;

  const sidebar = container.querySelector('#sctmg-shell-sidebar')
    || container.querySelector('.sctmg-shell__sidebar')
    || container.querySelector('aside.panel');
  const navList = container.querySelector('[data-shell-nav]')
    || container.querySelector('.sctmg-shell__nav');
  const content = container.querySelector('#main-content')
    || container.querySelector('#app-root')
    || container.querySelector('main')
    || doc.getElementById('app-root');

  if (!sidebar || !content) return null;
  return { container, sidebar, navList, content };
}

// Build a minimal shell scaffold for environments without the owned
// markup (tests, SSR fallback, legacy root). Mirrors the structure
// declared in src/index.html.
function buildShellScaffold(root) {
  const doc = (root && root.ownerDocument) || (typeof document !== 'undefined' ? document : null);
  if (!doc) return null;

  const container = doc.createElement('div');
  container.className = 'sctmg-shell';
  container.setAttribute('data-owned-shell', 'true');

  const header = doc.createElement('header');
  header.className = 'sctmg-shell__header';
  header.setAttribute('role', 'banner');

  const brand = doc.createElement('div');
  brand.className = 'sctmg-shell__brand';
  const brandLink = doc.createElement('a');
  brandLink.className = 'sctmg-shell__brand-link';
  brandLink.href = INITIAL_ROUTE_PATH;
  brandLink.textContent = 'sctmgUtils';
  brand.appendChild(brandLink);
  header.appendChild(brand);
  container.appendChild(header);

  const layout = doc.createElement('div');
  layout.className = 'sctmg-shell__layout';

  const sidebar = doc.createElement('nav');
  sidebar.className = 'sctmg-shell__sidebar';
  sidebar.setAttribute('aria-label', 'Secciones principales');

  const navList = doc.createElement('ul');
  navList.className = 'sctmg-shell__nav';
  navList.setAttribute('data-shell-nav', '');
  sidebar.appendChild(navList);

  const main = doc.createElement('main');
  main.id = 'app-root';
  main.className = 'sctmg-shell__content';
  main.setAttribute('tabindex', '-1');

  layout.appendChild(sidebar);
  layout.appendChild(main);
  container.appendChild(layout);

  const footer = doc.createElement('footer');
  footer.className = 'sctmg-shell__footer';
  const footerText = doc.createElement('span');
  footerText.className = 'sctmg-shell__footer-text';
  footerText.textContent = `sctmgUtils · runtime propio · Versión ${APP_VERSION}`;
  footer.appendChild(footerText);
  container.appendChild(footer);

  // Mount the scaffold around the provided root.
  root.appendChild(container);
  return { container, sidebar, navList, content: main };
}

// Wire the owned nav buttons declared in src/index.html so each
// <button data-shell-nav data-route="..."> routes through onNavigate.
// The owned markup is the single source of truth for nav composition;
// this function only attaches behaviour and active-state management.
//
// We always query from `sidebar` (the full nav container), not from the
// first `[data-shell-nav]` descendant. detectOwnedShell() currently
// returns the FIRST navigation button (a <button data-shell-nav>) as
// `navList`, so descending from it produces zero buttons. Using the
// sidebar as the query root fixes the "Game Rules" navigation bug and
// also keeps the fallback scaffold (which puts <ul data-shell-nav>
// inside the sidebar) working: querySelectorAll('button[data-shell-nav]')
// inside <aside>; querySelectorAll('ul[data-shell-nav] > li > button')
// in the scaffold both resolve via the same sidebar scope.
function populateSidebar(navList, sidebar, onNavigate) {
  if (!sidebar) return;
  const navScope = sidebar;
  const buttons = navScope.querySelectorAll('[data-shell-nav][data-route]');
  buttons.forEach((button) => {
    if (button.__sctmgShellNavBound) return;
    button.__sctmgShellNavBound = true;
    button.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const routePath = button.getAttribute('data-route');
      if (!routePath) return;
      event.preventDefault();
      const shell = button.closest('.sctmg-shell');
      if (shell?.__sctmgCloseDrawer && window.matchMedia?.('(max-width: 900px)').matches) {
        shell.__sctmgCloseDrawer();
      }
      onNavigate(routePath);
    });
  });
}

function markActive(scope, activeRoute) {
  if (!scope) return;
  const buttons = scope.querySelectorAll('[data-shell-nav][data-route]');
  buttons.forEach((button) => {
    if (!activeRoute || button.getAttribute('data-route') === activeRoute.path) {
      button.classList.add('active');
      button.setAttribute('aria-current', 'page');
    } else {
      button.classList.remove('active');
      button.removeAttribute('aria-current');
    }
  });
}

function bindDrawer(container) {
  if (!container || drawerBound) return;
  const toggle = container.querySelector('[data-shell-nav-toggle]');
  const scrim = container.querySelector('[data-shell-scrim]');
  if (!toggle) { drawerBound = true; return; }

  const setOpen = (open) => {
    container.setAttribute('data-drawer-open', open ? 'true' : 'false');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.setAttribute('aria-label', open ? 'Cerrar navegación principal' : 'Abrir navegación principal');
  };
  container.__sctmgCloseDrawer = () => setOpen(false);
  setOpen(false);

  toggle.addEventListener('click', () => {
    const isOpen = container.getAttribute('data-drawer-open') === 'true';
    setOpen(!isOpen);
  });

  if (scrim) {
    scrim.addEventListener('click', () => setOpen(false));
  }

  drawerBound = true;
}

function bindLoginButton(container) {
  const button = container?.querySelector('[data-shell-auth-login]');
  const overlay = document.getElementById('login-overlay');
  if (!button || !overlay || button.dataset.shellLoginBound === 'true') return;
  button.dataset.shellLoginBound = 'true';
  button.addEventListener('click', () => {
    overlay.style.display = 'flex';
    overlay.setAttribute('aria-hidden', 'false');
    overlay.querySelector('input')?.focus();
  });
}

function bindGuestLogin(container) {
  const button = document.getElementById('btn-guest-login');
  const overlay = document.getElementById('login-overlay');
  if (!button || !overlay || button.dataset.shellGuestBound === 'true') return;
  button.dataset.shellGuestBound = 'true';
  button.addEventListener('click', (event) => {
    event.preventDefault();
    window.__SCTMG_LOGIN_FLOW_ACTIVE = false;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    overlay.style.display = 'none';
    overlay.setAttribute('aria-hidden', 'true');
    const error = overlay.querySelector('#login-error');
    if (error) {
      error.textContent = '';
      error.style.display = 'none';
    }
  });
}

// Wiring for the Google login button declared in src/index.html with
// `data-shell-auth-google`. The runtime binds the button to the owned
// Firebase adapter (`signInWithGoogle`) without exposing inline
// `onclick` handlers. Bootstrapping never auto-authenticates; the call
// only fires on a real user click. Duplicate bindings are prevented via
// a dataset guard so the function is idempotent across shell reboots.
function showAuthOverlayError(message) {
  const overlay = document.getElementById('login-overlay');
  if (!overlay) return;
  const slot = overlay.querySelector('#login-error');
  if (!slot) return;
  const localised = message || (language === 'en'
    ? 'Google sign-in failed. Please try again.'
    : 'No se pudo iniciar sesión con Google. Inténtalo de nuevo.');
  slot.textContent = localised;
  slot.style.display = 'block';
  slot.setAttribute('role', 'alert');
}

export async function bindGoogleLogin(container, adapter) {
  const root = container || shellContainer;
  const button = root?.querySelector('[data-shell-auth-google]');
  const target = root || document;
  if (!button) return false;
  if (button.dataset.shellAuthGoogleBound === 'true') return true;
  button.dataset.shellAuthGoogleBound = 'true';
  const handler = async (event) => {
    if (event) {
      event.preventDefault();
    }
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    try {
      const invoke = typeof adapter === 'function' ? adapter : adapterSignInWithGoogle;
      await invoke(target);
    } catch (error) {
      console.error('[sctmgUtils] Google sign-in failed', error);
      showAuthOverlayError(error?.message);
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  };
  button.addEventListener('click', handler);
  return true;
}

function renderAuthenticatedUser(container, user) {
  const sidebar = container?.querySelector('#sctmg-shell-sidebar');
  const login = container?.querySelector('[data-shell-auth-login]');
  const authBlock = sidebar?.querySelector('[data-shell-auth-user]');
  if (!sidebar || !authBlock) return;
  const authenticated = Boolean(user && user.uid && !user.isAnonymous);
  if (authenticated) {
    const name = user.displayName || user.email || user.uid;
    const nameEl = authBlock.querySelector('[data-shell-user-name]');
    const userInfo = authBlock.querySelector('.sidebar-user-info');
    const logout = authBlock.querySelector('[data-shell-auth-logout]');
    if (nameEl) nameEl.textContent = name;
    if (userInfo && logout) {
      let row = userInfo.querySelector('.sidebar-user-row');
      if (!row) {
        row = document.createElement('div');
        row.className = 'sidebar-user-row';
        userInfo.appendChild(row);
      }
      if (nameEl && nameEl.parentElement !== row) row.appendChild(nameEl);
      if (logout.parentElement !== row) row.appendChild(logout);
      logout.classList.add('sidebar-logout-icon');
      logout.textContent = '';
      const icon = document.createElement('i');
      icon.className = 'fa-solid fa-power-off';
      icon.setAttribute('aria-hidden', 'true');
      logout.appendChild(icon);
    }
    authBlock.style.display = '';
    login?.style.setProperty('display', 'none');
    sidebar.appendChild(authBlock);
  } else {
    authBlock.style.display = 'none';
    login?.style.removeProperty('display');
  }
}

function closeLoginOverlay() {
  const overlay = document.getElementById('login-overlay');
  if (!overlay) return;
  overlay.style.display = 'none';
  overlay.setAttribute('aria-hidden', 'true');
  window.__SCTMG_LOGIN_FLOW_ACTIVE = false;
  const error = overlay.querySelector('#login-error');
  if (error) {
    error.textContent = '';
    error.style.display = 'none';
  }
}

function bindAuthState(container) {
  if (!container || container.dataset.shellAuthBound === 'true') return;
  container.dataset.shellAuthBound = 'true';
  const logout = container.querySelector('[data-shell-auth-logout]');
  logout?.addEventListener('click', async (event) => {
    event.preventDefault();
    logout.disabled = true;
    try {
      await adapterSignOut();
      renderAuthenticatedUser(container, null);
      window.dispatchEvent(new CustomEvent('sctmg:auth-changed', { detail: { user: null } }));
    } finally {
      logout.disabled = false;
    }
  });
  renderAuthenticatedUser(container, null);
  window.addEventListener('sctmg:auth-changed', (event) => {
    const user = event.detail?.user || event.detail || null;
    renderAuthenticatedUser(container, user);
    if (user && user.uid && !user.isAnonymous) closeLoginOverlay();
  });
  void adapterOnAuthStateChanged((user) => {
    renderAuthenticatedUser(container, user);
    if (user && user.uid && !user.isAnonymous) closeLoginOverlay();
    window.dispatchEvent(new CustomEvent('sctmg:auth-changed', { detail: { user } }));
  });
}

export async function initialiseAuth(container = shellContainer) {
  const overlay = document.getElementById('login-overlay');
  const pendingRedirect = sessionStorage.getItem('sctmg.googleRedirectPending') === '1';
  const suppressAutomaticLoginOverlay = import.meta.env?.DEV === true;
  if (pendingRedirect && overlay && !suppressAutomaticLoginOverlay) {
    overlay.style.display = 'flex';
    overlay.setAttribute('aria-hidden', 'false');
  }
  try {
    const user = pendingRedirect
      ? await adapterConsumeGoogleRedirect()
      : await adapterGetCurrentUser();
    renderAuthenticatedUser(container, user);
    if (user && user.uid && !user.isAnonymous) {
      if (overlay) {
        overlay.style.display = 'none';
        overlay.setAttribute('aria-hidden', 'true');
      }
      window.dispatchEvent(new CustomEvent('sctmg:auth-changed', { detail: { user } }));
    } else if (pendingRedirect && !suppressAutomaticLoginOverlay) {
      showAuthOverlayError();
    }
  } catch (error) {
    console.error('[sctmgUtils] authentication restore failed', error);
    if (pendingRedirect) showAuthOverlayError(error?.message);
  }
}

function syncFooterVersion(root) {
  const versionNode = root?.querySelector('[data-shell-version]')
    || document.querySelector('[data-shell-version]');
  if (versionNode) versionNode.textContent = APP_VERSION;
  if (typeof fetch !== 'function') return;
  fetch('/__sctmg_version', { cache: 'no-store' })
    .then((response) => response.ok ? response.json() : null)
    .then((payload) => {
      if (payload?.version && versionNode) versionNode.textContent = payload.version;
    })
    .catch(() => {
      // The production static build keeps the injected version fallback.
    });
}

function bootShell(root) {
  if (!root) throw new Error('[sctmgUtils] bootShell requires a root element');
  if (shellRoot === root && shellContainer) return root;

  const detected = detectOwnedShell(root);
  if (detected) {
    shellContainer = detected.container;
    shellSidebar = detected.sidebar;
    shellNavList = detected.navList;
    shellContent = detected.content;
  } else {
    const built = buildShellScaffold(root);
    if (!built) throw new Error('[sctmgUtils] bootShell could not create the shell scaffold');
    shellContainer = built.container;
    shellSidebar = built.sidebar;
    shellNavList = built.navList;
    shellContent = built.content;
  }

  populateSidebar(shellNavList, shellSidebar, navigate);
  bindDrawer(shellContainer);
  bindLoginButton(shellContainer);
  bindGuestLogin(shellContainer);
  bindGoogleLogin(shellContainer);
  bindAuthState(shellContainer);
  syncFooterVersion(shellContainer);
  shellRoot = root;
  return root;
}

function getContent() {
  return shellContent;
}

async function teardownCurrent({ clearContent = true } = {}) {
  if (!currentRoute || !currentRoute.instance) return;
  const { route, instance } = currentRoute;
  if (instance && typeof instance.unmount === 'function') {
    try { await instance.unmount(); }
    catch (error) { console.error(`[sctmgUtils] unmount failed for ${route.id}`, error); }
  }
  const content = getContent();
  if (clearContent && content) content.replaceChildren();
  currentRoute = null;
}

async function mountRouteById(routeId, { replace = false, path, initialFaction, initialCategory, initialCardId, campaignId } = {}) {
  const baseRoute = ROUTES_BY_ID.get(routeId) || ROUTES_BY_ID.get(INITIAL_ROUTE_ID);
  const route = baseRoute ? { ...baseRoute, ...(path ? { path, initialFaction, initialCategory, initialCardId, campaignId } : {}) } : baseRoute;
  const content = getContent();
  if (!content || !route) return null;

  // Contextual navigation inside one feature stays SPA-local: preserve its
  // loaded catalog, React roots and styles, and only update route state.
  if (currentRoute && currentRoute.id === route.id && typeof currentRoute.instance?.updateRoute === 'function') {
    await currentRoute.instance.updateRoute(route);
    currentRoute.path = route.path;
    currentRoute.route = route;
    markActive(shellNavList, route);
    markActive(shellSidebar, route);
    if (replace) history.replaceState({ routeId: route.id }, '', route.path);
    else history.pushState({ routeId: route.id }, '', route.path);
    return currentRoute;
  }

  const staging = content.ownerDocument.createElement('div');
  staging.className = 'sctmg-route-staging';
  const previousRoute = currentRoute;

  const mod = await loadRouteModule(route);
  const factory = mod && (mod.mount || mod.default);
  let instance = null;
  if (typeof factory === 'function') {
    route.onPathChange = (nextPath) => {
      if (typeof nextPath !== 'string' || !nextPath.startsWith('/factions/')) return;
      if (route.path === nextPath) return;
      void navigate(nextPath).catch((error) => {
        console.error('[sctmgUtils] faction navigation failed', error);
      });
    };
    instance = await factory({ root: staging, language, route });
  }

  if (previousRoute) await teardownCurrent({ clearContent: false });
  // Keep the staging element itself in the document. React roots retain
  // their host container; moving only its children breaks later commits.
  content.replaceChildren(staging);
  content.dataset.route = route.id;
  currentRoute = { id: route.id, path: route.path, instance, route };
  markActive(shellNavList, route);
  markActive(shellSidebar, route);
  // Preserve FAQ query deep links during the initial/popstate remount; the
  // feature owns the `faq` and `lang` parameters after it is mounted.
  const browserPath = replace && route.id === 'faqs'
    ? `${route.path}${window.location.search}${window.location.hash}`
    : route.path;
  if (replace) history.replaceState({ routeId: route.id }, '', browserPath);
  else history.pushState({ routeId: route.id }, '', route.path);
  return currentRoute;
}

async function navigate(rawPath) {
  const route = findRouteByPath(rawPath);
  if (!route) {
    await mountRouteById(INITIAL_ROUTE_ID, { replace: true });
    return currentRoute;
  }
  return mountRouteById(route.id, {
    path: route.path,
    initialFaction: route.initialFaction,
    initialCategory: route.initialCategory,
    initialCardId: route.initialCardId,
    campaignId: route.campaignId,
  });
}

function handlePopState() {
  const route = findRouteByPath(window.location.pathname);
  if (route) mountRouteById(route.id, {
    replace: true,
    path: route.path,
    initialFaction: route.initialFaction,
    initialCategory: route.initialCategory,
    initialCardId: route.initialCardId,
    campaignId: route.campaignId,
  });
  else mountRouteById(INITIAL_ROUTE_ID, { replace: true });
}

function startRouter() {
  window.addEventListener('popstate', handlePopState);
}

function stopRouter() {
  window.removeEventListener('popstate', handlePopState);
}

async function mountInitialRoute(root) {
  if (root && (shellRoot !== root || !shellContainer)) bootShell(root);
  startRouter();
  const initialPath = normalisePath(window.location.pathname);
  const route = findRouteByPath(initialPath);
  const target = route ? route.id : INITIAL_ROUTE_ID;
  return mountRouteById(target, {
    replace: true,
    path: route?.path,
    initialFaction: route?.initialFaction,
    initialCategory: route?.initialCategory,
    initialCardId: route?.initialCardId,
    campaignId: route?.campaignId,
  });
}

async function setLanguage(nextLanguage) {
  if (!nextLanguage || nextLanguage === language) return;
  language = nextLanguage;
  // Buttons are owned by the markup so labels are static; just refresh
  // the active state in case the user changed language after navigation.
  if (currentRoute) {
    markActive(shellNavList, currentRoute.route);
    markActive(shellSidebar, currentRoute.route);
    if (currentRoute.instance && typeof currentRoute.instance.setLanguage === 'function') {
      return currentRoute.instance.setLanguage(language);
    }
  }
}

export {
  bootShell,
  mountInitialRoute,
  navigate,
  startRouter,
  stopRouter,
  setLanguage,
  normalisePath,
  findRouteByPath,
};

export const __test__ = { ROUTES, ROUTES_BY_ID, ROUTES_BY_PATH, INITIAL_ROUTE_ID };